<template>
    <router-link :to="`/poll/${poll.id}`"
        :class="['poll-tile', { 'poll-tile--large': isLarge }]"
    >
        <div v-if="isLarge"
            class="poll-tile__partner"
        >
            <partner-logo :url="pollPartner.partnerLogo"
                class="poll-tile__partner-logo"
            />

            <div class="poll-tile__partner-shortname">{{ pollPartner.shortName }}</div>
            <div class="poll-tile__partner-name">{{ pollPartner.name }}</div>
        </div>

        <div class="poll-tile__labels">
            <div :class="['poll-tile__labels-date', `poll-tile__labels-date--${poll.status}`]">
                <i class="icon icon-calendar" />
                {{ date }}
            </div>

            <div v-if="poll.isBound"
                class="poll-tile__labels-icon"
            >
                <i class="icon icon-binding" />
            </div>

            <div v-if="isCappedLimit"
                class="poll-tile__labels-icon"
            >
                <i class="icon icon-cap" />
            </div>
        </div>

        <div :class="['poll-tile__question', { 'poll-tile__question--bordered': isLarge }]">
            {{ question }}
        </div>

        <div v-if="isLarge"
            class="poll-tile__vote-status"
        >
            <i :class="['icon', 'icon-check', { 'icon--green': hasVoted }]" />
            <div>{{ $t(statusText.text) }} <strong>{{ $t(statusText.prompt) }}</strong></div>
        </div>
    </router-link>
</template>

<script>
// 3rd Party
import {
    mapGetters,
} from 'vuex';

// Config
import {
    pollClosedStatuses,
} from '@shared/config/poll-statuses.js';

// Utils
import {
    format,
} from '@shared/utils/dates.js';

// Components
import partnerLogo from '@webapp/components/partner-logo/partner-logo.vue';

export default {
    name: 'poll-tile',
    components: {
        partnerLogo,
    },
    props: {
        partner: {
            type: Object,
            default: () => ({}),
        },
        poll: {
            type: Object,
            default: () => ({}),
        },
        isLarge: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapGetters(['getTranslatedStr']),

        ...mapGetters('partners', ['partnerById']),

        pollPartner() {
            return Object.keys(this.partner).length
                ? this.partner
                : this.partnerById(this.poll.partnerId);
        },

        date() {
            return this.poll.status === 'scheduled'
                ? format(this.poll.openAt, 'dateShort')
                : format(this.poll.closeAt, 'dateShort');
        },
        isClosed() {
            return pollClosedStatuses.includes(this.poll.status);
        },
        question() {
            return this.getTranslatedStr(this.poll, 'question', this.poll.question).trim();
        },
        isCappedLimit() {
            return this.poll.currencyId && this.poll.tokenLimit > 0;
        },
        hasVoted() {
            return this.poll.userVote;
        },
        // TODO: Clarify options
        statusText() {
            let prompt = 'polls.pollNotOpenedYet',
                text = 'polls.previewPoll';

            switch (this.poll.status) {
                case 'scheduled':
                    text = 'polls.pollNotOpenedYet';
                    prompt = 'polls.previewPoll';
                    break;
                case 'opened':
                    if (this.hasVoted) {
                        text = 'polls.voted';
                        prompt = 'polls.viewVotingProgress';
                    } else {
                        text = 'polls.notYetVoted';
                        prompt = 'polls.voteNow';
                    }
                    break;
                case 'closed':
                    text = 'polls.pollClosed';
                    prompt = 'polls.viewResults';
                    break;
                default:
                    break;
            }

            return {
                text,
                prompt,
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.poll-tile {
    width: 26rem;
    height: 18rem;
    display: flex;
    flex-direction: column;
    background: $white-color;
    margin: 0.5rem 1rem 2rem;
    border-radius: 0.8rem;
    box-shadow: 0 1rem 1rem rgba($black-color, 0.08);
    padding: 2rem;
    text-decoration: none;
    color: $cyprus-color;

    @include max-width(sm) {
        width: 22rem;
        margin: 0;
    }

    &--large {
        flex-wrap: wrap;
        flex-direction: row;
        position: relative;
        width: 100%;
        height: auto;
        min-height: 14rem;
        padding-left: 8.5rem;
        padding-bottom: 0;
        margin: 2rem 1rem;
    }

    &:hover {
        cursor: pointer;
        opacity: 0.8;
    }

    &__partner {
        color: $cyprus-color;

        @include min-width(md) {
            width: 50%;
        }

        &-logo {
            position: absolute;
            left: 2rem;
            width: 4.5rem;
            height: 4.5rem;
            margin-top: -0.25rem;
        }

        &-shortname,
        &-name {
            font-size: 1.5rem;
        }

        &-shortname {
            font-weight: bold;
        }
    }

    &__partner + &__labels {
        align-items: center;
        justify-content: flex-start;
        margin-top: 0.75rem;
        margin-bottom: -1.5rem;

        @include min-width(md) {
            margin: 0;
            width: 50%;
            justify-content: flex-end;
            order: initial;
        }
    }

    &__labels {
        display: flex;
        width: 100%;
        margin-bottom: 2rem;

        &-date,
        &-icon {
            margin-right: 0.8rem;
            border-radius: 0.4rem;
            height: 2.25rem;
            display: flex;
            align-items: center;
        }

        &-date {
            color: $white-color;
            font-weight: bold;
            padding: 0 0.5rem;

            &--closed,
            &--closed-pending,
            &--closed-blockchain-pending,
            &--closed-release-wallet-limit,
            &--expired {
                background-color: $red-orange-color;
            }

            &--scheduled {
                background-color: $gorse-color;
                color: $black-color;
            }

            &--opened {
                background-color: $lima-color;
            }

            .icon {
                margin-right: 0.5rem;
            }
        }

        &-icon {
            color: $white-color;
            background: $indigo-color;
            width: 2.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .icon {
            font-size: 1.4rem;
        }
    }

    &__question {
        width: 100%;
        font-size: 1.5rem;
        line-clamp: 4;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        /* stylelint-disable */
        -webkit-box-orient: vertical;
        /* stylelint-enable */
        overflow: hidden;

        &--bordered {
            border-top: 0.1rem solid $porcelain-color;
            border-bottom: 0.1rem solid $porcelain-color;
            margin-top: 3.5rem;
            display: flex;
            align-items: center;
            padding: 2rem 0;
            color: $cyprus-color;
        }
    }

    &__vote-status {
        display: flex;
        align-items: center;
        padding: 2rem 0;
        font-size: 1.5rem;
        color: $dusty-gray-color;

        .icon {
            font-size: 2.4rem;
            margin-right: 0.5rem;

            &--green {
                color: $lima-color;
            }
        }

        strong {
            color: $lima-color;
        }
    }
}
</style>